import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DateCalendar } from "@mui/x-date-pickers";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { Popover, Box, Typography, IconButton, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

// Custom Hooks
import { useGoogleFitLogin } from "@utils/useGoogleFitLogin";

// Utils
import {
  processApiResponse,
  processWeekData,
  processSummaryStats,
  isDateEnabled,
  findWeekIndexForDate,
} from "@utils/DeviceDataProcessor";

// Components
import useMediaQueryHook from "@hooks/useMediaQueryHook";
import { ConnectGoogle } from "@components/common/Modals/ConnectGoogle";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { getDeviceCards, getStatCards } from "data/uiData/DeviceCards";
import DonutChart from "@components/ui/charts/DonutChart";
import LogoutDevice from "./LogoutDevice";
import FourCardsView from "@components/ui/FourCardsView";
import AISuggestion from "@components/common/StepsAi";

// Images
import {
  BackImg,
  GoogleWatch,
  GoogleHeart,
  OrangeDart,
} from "@utils/defaultImages";
import { useDeviceChart } from "@hooks/useDeviceChart";
import WeekNavigation from "@features/DeviceIntegrations/WeekNavigation";
import NoDataState from "@features/DeviceIntegrations/NoDataState";
import ChartSection from "@features/DeviceIntegrations/ChartSection";
import SyncInfo from "@features/DeviceIntegrations/SyncInfo";
import LoadingOverlay from "@features/DeviceIntegrations/LoadingOverlay";
import dayjs from "dayjs";

const ExercisePlanCharts = ({ showLoader = false, setShowLoader }) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQueryHook();

  // Custom hook for state management
  const {
    datesAvailable,
    setDatesAvailable,
    googleFitConnect,
    email,
    open,
    setOpen,
    connectingGoogleFit,
    dataLoading,
    hasData,
    setHasData,
    lastSyncTime,
    setLastSyncTime,
    logoutLoading,
    currentWeekIndex,
    setCurrentWeekIndex,
    weeklyData,
    setWeeklyData,
    setOverallData,
    calendarAnchor,
    setCalendarAnchor,
    calendarDate,
    setCalendarDate,
    currentWeekRange,
    setCurrentWeekRange,
    chartData,
    setChartData,
    goals,
    setGoals,
    summaryStats,
    setSummaryStats,
    handleFetchData,
    handleSyncNow,
    handleSuccess,
    handleLogout,
    hasFetchedAfterConnect,
  } = useDeviceChart();

  const [activeChartTab, setActiveChartTab] = useState("steps");
  const [syncButtonLoading, setSyncButtonLoading] = useState(false);

  const statCards = getStatCards(summaryStats);
  const physicalCharactersticsCard = getDeviceCards(summaryStats);
  const loginGoogleFit = useGoogleFitLogin(handleSuccess);

  // Enhanced data fetching function that uses hook's handleFetchData
  const fetchAndProcessData = async () => {
    try {
      const response = await handleFetchData();
      if (response?.weekly_summaries?.length > 0) {
        const sortedWeeklyData = processApiResponse(
          response,
          setWeeklyData,
          setOverallData,
          setGoals
        );
        setDatesAvailable(response?.dates_available);
        showWeekData(0, sortedWeeklyData);
        if (response.last_sync) setLastSyncTime(response.last_sync);
        setHasData(true);
      } else {
        setHasData(false);
      }
    } catch (error) {
      console.error("Error processing data:", error);
      setHasData(false);
    } finally {
      if (setShowLoader) setShowLoader(false);
      setSyncButtonLoading(false);
    }
  };

  // Show data for a specific week
  const showWeekData = (weekIndex, weeklySummaries = weeklyData) => {
    if (!weeklySummaries || weeklySummaries.length === 0) return;

    const weekData = weeklySummaries[weekIndex];
    setCurrentWeekIndex(weekIndex);
    processWeekData(
      weekData,
      setCurrentWeekRange,
      setCalendarDate,
      setChartData,
      (summary) => processSummaryStats(summary, setSummaryStats)
    );
    setHasData(true);
  };

  // Navigation handlers
  const handlePreviousWeek = () => {
    if (currentWeekIndex < weeklyData.length - 1) {
      showWeekData(currentWeekIndex + 1);
    }
  };

  const handleNextWeek = () => {
    if (currentWeekIndex > 0) {
      showWeekData(currentWeekIndex - 1);
    }
  };

  const handleOpenCalendar = (event) => {
    setCalendarAnchor(event.currentTarget);
  };

  const handleCloseCalendar = () => setCalendarAnchor(null);

  const handleDateSelect = (date) => {
    if (isDateEnabled(date, weeklyData)) {
      const weekIndex = findWeekIndexForDate(date, weeklyData);
      if (weekIndex !== -1) showWeekData(weekIndex);
      handleCloseCalendar();
    }
  };

  // Custom calendar day rendering
  const renderDay = (day, selectedDays, pickersDayProps) => {
    const enabled = isDateEnabled(day, weeklyData);
    const isCurrentWeek =
      currentWeekRange.startDate &&
      currentWeekRange.endDate &&
      day >= currentWeekRange.startDate &&
      day <= currentWeekRange.endDate;

    return (
      <PickersDay
        {...pickersDayProps}
        disabled={!enabled}
        day={day}
        sx={{
          ...(enabled && {
            backgroundColor: isCurrentWeek ? "#0575E6" : "transparent",
            color: isCurrentWeek ? "white" : "inherit",
            "&:hover": {
              backgroundColor: isCurrentWeek ? "#0461c4" : "#f0f0f0",
            },
            "&.Mui-selected": { backgroundColor: "#0575E6", color: "white" },
            "&.Mui-disabled": { opacity: 0.3 },
          }),
        }}
      />
    );
  };

  const formatWeekDisplay = () => {
    if (currentWeekRange.startDate && currentWeekRange.endDate) {
      return `${format(currentWeekRange.startDate, "dd MMM")} - ${format(
        currentWeekRange.endDate,
        "dd MMM yyyy"
      )}`;
    }
    return "Loading...";
  };

  // Google Fit connection
  const handleOpenGoogle = async () => {
    try {
      loginGoogleFit();
    } catch (error) {
      console.error("Error in google authentication", error);
    }
  };

  // Manual sync handler with button loading state
  const handleManualSync = async () => {
    if (dataLoading || syncButtonLoading) return;

    setSyncButtonLoading(true);
    // Pass true to handleFetchData to indicate this is a "silent" background sync
    const response = await handleFetchData(true);

    if (response) {
      // Re-process the data to update the UI with new sync values
      const sortedWeeklyData = processApiResponse(
        response,
        setWeeklyData,
        setOverallData,
        setGoals
      );
      showWeekData(0, sortedWeeklyData);
    }
    setSyncButtonLoading(false);
  };

  // Effects
  useEffect(() => {
    // Only fetch data if connected AND hasn't been fetched after connection
    if (googleFitConnect && !hasFetchedAfterConnect && !dataLoading) {
      console.log("Initial data fetch after connection");
      fetchAndProcessData();
    }
  }, [googleFitConnect]); // Remove hasFetchedAfterConnect and dataLoading from dependencies

  // Update the useEffect to handle showLoader
  useEffect(() => {
    if (showLoader && setShowLoader && !dataLoading) {
      setShowLoader(false);
    }
  }, [dataLoading, showLoader, setShowLoader]);

  const shouldDisableDate = (date) => {
    if (!datesAvailable.length) return true;

    const dateString = dayjs(date).format("YYYY-MM-DD");
    return !datesAvailable.includes(dateString);
  };

  if (showLoader) return <ActualDietPlanLoader progressTracker={true} />;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div
        className={`bg-cover p-6 rounded-3xl text-white min-h-[500px] relative ${
          (connectingGoogleFit || dataLoading) && !hasData
            ? "opacity-50 pointer-events-none"
            : ""
        }`}
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        {!googleFitConnect ? (
          <ConnectScreen
            connectingGoogleFit={connectingGoogleFit}
            setOpen={setOpen}
            handleOpenGoogle={handleOpenGoogle}
          />
        ) : (
          <DataScreen
            dataLoading={dataLoading && !syncButtonLoading} // Only show global overlay if sync isn't active
            syncButtonLoading={syncButtonLoading}
            shouldDisableDate={shouldDisableDate}
            hasData={hasData}
            connectingGoogleFit={connectingGoogleFit}
            weeklyData={weeklyData}
            currentWeekIndex={currentWeekIndex}
            handlePreviousWeek={handlePreviousWeek}
            handleNextWeek={handleNextWeek}
            formatWeekDisplay={formatWeekDisplay}
            handleOpenCalendar={handleOpenCalendar}
            lastSyncTime={lastSyncTime}
            handleSyncNow={handleManualSync}
            email={email}
            logoutLoading={logoutLoading}
            handleLogout={handleLogout}
            statCards={statCards}
            activeChartTab={activeChartTab}
            setActiveChartTab={setActiveChartTab}
            chartData={chartData}
            isMobile={isMobile}
            goals={goals}
            physicalCharactersticsCard={physicalCharactersticsCard}
            summaryStats={summaryStats}
            currentWeekRange={currentWeekRange}
            calendarAnchor={calendarAnchor}
            handleCloseCalendar={handleCloseCalendar}
            calendarDate={calendarDate}
            handleDateSelect={handleDateSelect}
            renderDay={renderDay}
          />
        )}

        {open && (
          <ConnectGoogle
            open={open}
            setOpen={setOpen}
            handleOpenGoogle={handleOpenGoogle}
            loading={connectingGoogleFit}
          />
        )}
      </div>
    </LocalizationProvider>
  );
};

// Sub-components for better organization
const ConnectScreen = ({ connectingGoogleFit, setOpen, handleOpenGoogle }) => (
  <>
    <div className="flex justify-center items-center flex-col">
      <img src={GoogleWatch} alt="Watch Image" className="min-h-[200px]" />
      <h1 className="text-[20px] font-[600] text-white font-[Lato] mt-3">
        Set Up Your Activity Tracking
      </h1>
      <p className="text-[16px] font-[Lato] font-[400] mt-3">
        Enable syncing to see your activity progress here.
      </p>
      <button
        className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full mt-6 flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => setOpen(true)}
        disabled={connectingGoogleFit}
      >
        {connectingGoogleFit ? "Connecting..." : "Connect via Google Fit"}
        {!connectingGoogleFit && <img src={GoogleHeart} alt="GoogleHeart" />}
      </button>
    </div>
    {connectingGoogleFit && (
      <LoadingOverlay isLoading={true} message="Connecting to Google Fit..." />
    )}
  </>
);

const DataScreen = ({
  hasData,
  dataLoading,
  connectingGoogleFit,
  weeklyData,
  currentWeekIndex,
  handlePreviousWeek,
  handleNextWeek,
  formatWeekDisplay,
  handleOpenCalendar,
  lastSyncTime,
  handleSyncNow,
  syncButtonLoading,
  email,
  logoutLoading,
  handleLogout,
  statCards,
  activeChartTab,
  setActiveChartTab,
  chartData,
  isMobile,
  goals,
  physicalCharactersticsCard,
  summaryStats,
  currentWeekRange,
  calendarAnchor,
  handleCloseCalendar,
  calendarDate,
  handleDateSelect,
  renderDay,
  shouldDisableDate,
}) => (
  <>
    {/* Loading overlays - These will show on top when loading */}
    <LoadingOverlay
      isLoading={dataLoading && !syncButtonLoading}
      message="Loading data..."
    />
    <LoadingOverlay isLoading={connectingGoogleFit} message="Connecting..." />

    {/* Show content only when NOT loading and HAS data */}
    {!dataLoading && hasData && weeklyData.length > 0 ? (
      <>
        {/* Header Section */}
        <h2 className="text-[16px] font-[500] font-[Lato] mb-2 md:mb-3 text-center">
          Your Connected Health Stats
        </h2>

        <div className="flex flex-col md:flex-row justify-between md:items-center mb-2 md:mb-6 gap-2">
          <div className="flex flex-col xl:flex-row xl:items-center md:justify-end gap-2 flex-1">
            <WeekNavigation
              currentWeekIndex={currentWeekIndex}
              weeklyData={weeklyData}
              currentWeekRange={currentWeekRange}
              dataLoading={dataLoading}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              onOpenCalendar={handleOpenCalendar}
              formatWeekDisplay={formatWeekDisplay}
            />
            <SyncInfo
              lastSyncTime={lastSyncTime}
              onSyncNow={handleSyncNow}
              dataLoading={syncButtonLoading}
            />
            <LogoutDevice
              logoutLoading={logoutLoading}
              email={email}
              onLogout={handleLogout}
            />
          </div>
        </div>

        {/* Calendar Popover */}
        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={handleCloseCalendar}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{
            "& .MuiPopover-paper": {
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#0575E6", mb: 2 }}
            >
              Select Week
            </Typography>
            <DateCalendar
              value={calendarDate}
              onChange={handleDateSelect}
              renderDay={renderDay}
              shouldDisableDate={shouldDisableDate}
              sx={{ "& .MuiPickersDay-root": { fontSize: "0.875rem" } }}
              disabled={dataLoading}
            />
            {/* <Typography
              variant="caption"
              sx={{ color: "#666", mt: 2, display: "block" }}
            >
              Note: Only weeks with data are selectable
            </Typography> */}
          </Box>
        </Popover>

        {/* Main Content */}
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-10 xl:gap-20">
            {statCards.map((stat, index) => (
              <StatCard key={index} stat={stat} />
            ))}
          </div>

          {/* Chart Section */}
          <div className="mt-2 md:mt-6 grid grid-cols-1">
            <ChartSection
              activeChartTab={activeChartTab}
              setActiveChartTab={setActiveChartTab}
              chartData={chartData}
              isMobile={isMobile}
              goals={goals}
            />
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 xl:grid-cols-2 mt-2 md:mt-6 gap-3">
            <FourCardsView
              data={physicalCharactersticsCard}
              columns={4}
              gap="gap-2"
              rounded="rounded-[17px]"
            />

            {/* Suggestion Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:mt-3 xl:mt-0">
              <div className="bg-white rounded-[17px] h-full p-4">
                <div className="flex items-center gap-3">
                  <OrangeDart className="w-5 h-5" />
                  <h2 className="font-[600] font-[Lato] text-[12px] text-[#444444]">
                    Your fitness goal progress this week
                  </h2>
                </div>

                <DonutChart
                  percentage={summaryStats.weeklyGoalProgress}
                  title="Weekly Goal"
                />
                <GoalProgressDisplay
                  summaryStats={summaryStats}
                  goals={goals}
                />
              </div>
              <AISuggestion
                fitnessData={summaryStats}
                dateRange={{
                  startDate: currentWeekRange.startDate,
                  endDate: currentWeekRange.endDate,
                  daysSelected: chartData.steps?.length || 0,
                }}
                chartData={chartData}
              />
            </div>
          </div>
        </>
      </>
    ) : !dataLoading && !hasData ? (
      // Show NoDataState only when NOT loading and NO data
      <NoDataState />
    ) : null}
  </>
);

const StatCard = ({ stat }) => (
  <div className="bg-white rounded-2xl p-4 text-[#0575e6] flex flex-col xl:flex-row items-center justify-center gap-3">
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center"
      style={{ backgroundColor: stat.color }}
    >
      {stat.icon}
    </div>
    <div>
      <div className="text-[12px] font-[Lato] text-[#898787] text-center xl:text-start">
        {stat.label}
      </div>
      <div className="text-[#6C6A6A] text-[16px] font-[Lato] text-center xl:text-start">
        {stat.value}
      </div>
    </div>
  </div>
);

const GoalProgressDisplay = ({ summaryStats, goals }) => (
  <>
    <div className="flex items-center justify-center gap-1 my-2">
      <div className="bg-[#FF8B38] w-4 h-4 rounded-full"></div>
      <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
        Goal:
      </h2>
      <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
        {goals?.step_goal * 7} steps per week
      </p>
    </div>
    <div className="flex items-center justify-center gap-1">
      <div className="bg-[#05CFE6] w-4 h-4 rounded-full"></div>
      <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
        Completed:
      </h2>
      <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
        {summaryStats?.totalSteps} steps
      </p>
    </div>
  </>
);

export default ExercisePlanCharts;
